import moment from 'moment';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { includes } from 'lodash';
import { usePostHog } from 'posthog-js/react';
import { Formik } from 'formik';
import { useCalendar } from '../../CalendarContext/CalendarContext';
import {
  AccountData,
  OrderData,
  ResourceHoldData,
  OrderPageTypes,
  OrderService,
  VoucherOut,
  VoucherService,
  VoucherType,
  WaitingListItemData,
} from '../../../api';
import { useLanguage } from '../../../common/GeneralUtils';
import { CalendarView, DATE_FORMAT } from '../../calendarWrapper/CalendarWrapper';
import { useAuth } from '../../../session/InternalAuthProvider';
import { ChooseWorkshopSelect } from './components/chooseWorkshopSelect/ChooseWorkshopSelect';
import { ExperienceDetailPanel } from './components/experienceDetailPanel/ExperienceDetailPanel';
import { DateAndTimePanel } from './components/dateAndTimePanel/DateAndTimePanel';
import { PersonalDetailsPanel } from './components/personalDetailsPanel/PersonalDetailsPanel';
import { ManagementPanel } from './components/managementPanel/ManagementPanel';
import { SeatingPanel } from './components/seatingPanel/SeatingPanel';
import { CommunicationTrackingPanel } from './components/communicationTrackingPanel/CommunicationTrackingPanel';
import { PaymentLinksPanel } from './components/paymentLinksPanel/PaymentLinksPanel';
import { GuestRelationshipBanner } from './components/GuestRelationshipBanner';
import { useAssignmentConflictHandler } from '../../../common/apiNotifications/responseHandlers/useAssignmentConflictHandler';
import { useEditOrderDates } from './hooks/useEditOrderDates';
import { useVoucherDialogs } from './hooks/useVoucherDialogs';
import { EditOrderDialogFooter, EditOrderDialogIntent } from './components/footer/EditOrderDialogFooter';
import { EditOrderFormValues, EditOrderPanels } from './EditOrderTypes';
// @ts-ignore
import { useEditOrder } from './EditOrderContext';
import { getOrderPages } from '../../../utils/AccountUtils';
import { VoucherRedemptionsPanel } from './components/voucherRedemptionsPanel/VoucherRedemptionsPanel';
import { canonicalHolds } from '../../utils/orderSeating';
import { holdExpiryIso, isPendingOrder } from '../../../common/pendingOrderUtils';
import { getInitialOrder } from './orderForm/orderFormDefaults';
import { buildOrderFormInitialValues } from './orderForm/orderFormValues';
import { buildOrderValidationSchema } from './orderForm/orderValidation';
import { getNewParentId } from './orderForm/createOrderPayload';
import { OrderFormSync } from './orderForm/OrderFormSync';

enum DateTimeError {
  MISSING_DATE = 'MISSING_DATE',
  MISSING_TIME = 'MISSING_TIME',
}

function mapError(dateTimeError: DateTimeError, t: any) {
  switch (dateTimeError) {
    case DateTimeError.MISSING_DATE:
      return t('calendar.edit_order.errors.missingDate');
    case DateTimeError.MISSING_TIME:
      return t('calendar.edit_order.errors.missingTime');
    default:
      return t('calendar.edit_order.errors.missingDate');
  }
}

type OrderDialogContentProps = {
  editOrderDialogIntent: EditOrderDialogIntent;
  expandedPanel?: EditOrderPanels;
  setExpandedPanel: (panelName?: EditOrderPanels) => void;
  updateOrder?: (order: OrderData) => void;
  focusExtraItems?: boolean;
  /** Generic scroll-to-element id (a comment field or the resource picker), brought into view once its panel expands. */
  focusCommentFieldId?: string;
  closeEditOrderDialog: any;
  editOrderDialogState: any;
  voucherToRedeem?: VoucherOut;
  voucherToEdit?: VoucherOut;
  waitingListItem?: WaitingListItemData;
};

export const OrderDialogContent = ({
  editOrderDialogIntent,
  expandedPanel,
  setExpandedPanel,
  updateOrder,
  focusExtraItems,
  focusCommentFieldId,
  closeEditOrderDialog,
  editOrderDialogState,
  voucherToRedeem,
  voucherToEdit,
  waitingListItem,
}: OrderDialogContentProps) => {
  const posthog = usePostHog();
  const { users, currentDate, calendarView } = useCalendar();
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!focusCommentFieldId || !editOrderDialogState.isOpen) {
      return undefined;
    }
    /**
     * The panel expands (and the panels above it may collapse) after this render. Mid-animation the
     * field can sit still while the content below it is still growing, and the container cannot
     * scroll past content that is not there yet, so a scroll at a fixed delay stopped short. Wait
     * until both the field's position and the scrollable height have held still for a few checks,
     * then scroll once. Gives up after a couple of seconds.
     */
    const CHECK_INTERVAL_MS = 50;
    const STABLE_CHECKS = 4;
    const MAX_WAIT_MS = 2000;
    const startedAt = performance.now();
    let lastLayout: string | null = null;
    let stableChecks = 0;
    let interval: ReturnType<typeof setInterval> | undefined;

    const waitForLayout = () => {
      const field = scrollContainerRef.current?.querySelector(`#${CSS.escape(focusCommentFieldId)}`);
      const layout = field ? `${field.getBoundingClientRect().top}:${scrollContainerRef.current?.scrollHeight}` : null;
      stableChecks = layout !== null && layout === lastLayout ? stableChecks + 1 : 0;
      lastLayout = layout;
      const timedOut = performance.now() - startedAt > MAX_WAIT_MS;
      if (field && (stableChecks >= STABLE_CHECKS || timedOut)) {
        field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      if ((field && stableChecks >= STABLE_CHECKS) || timedOut) {
        clearInterval(interval);
      }
    };
    interval = setInterval(waitForLayout, CHECK_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [focusCommentFieldId, editOrderDialogState.isOpen]);
  const { executeWithConflictCheck } = useAssignmentConflictHandler();

  // @ts-ignore
  const { editOrderState, setOrderCustomDetails, setOrderPage } = useEditOrder();
  const isEventOrCourse = includes(
    [OrderPageTypes.COURSE, OrderPageTypes.EVENT],
    editOrderState.orderPage.order_page_type,
  );
  const isCourse = editOrderState.orderPage.order_page_type === OrderPageTypes.COURSE;
  const { t } = useTranslation();
  const { parentId, parent, onSubmitCallback } = editOrderDialogState;
  let order = editOrderDialogState.order as Partial<OrderData>;

  const language = useLanguage();
  let isEditOrder = true;
  const isWaitlistItem = !!waitingListItem;
  if (!order) {
    order = getInitialOrder(
      parent,
      !isEventOrCourse && !isWaitlistItem,
      [CalendarView.DAY, CalendarView.AREAS].includes(calendarView) ? currentDate : moment().format(DATE_FORMAT),
      voucherToRedeem || voucherToEdit,
      editOrderDialogState.slot,
    );
    isEditOrder = false;
  }

  const isOneOfVoucherIntents = includes(
    [EditOrderDialogIntent.CreateVoucher, EditOrderDialogIntent.EditVoucher],
    editOrderDialogIntent,
  );

  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const orderPages = getOrderPages(authState.account);
  const isMonetaryVoucher = isOneOfVoucherIntents && editOrderState.voucherType === VoucherType.MONETARY;

  const [loading, setLoading] = useState(false);
  const [dateTimeError, setDateTimeError] = useState<DateTimeError | undefined>(undefined);

  const entityKey = order?.id ?? voucherToEdit?.id ?? waitingListItem?.id ?? 'new';

  const isPendingEditedOrder =
    editOrderDialogIntent === EditOrderDialogIntent.EditOrder && !!order.status && isPendingOrder(order as OrderData);

  const initialValues: EditOrderFormValues = useMemo(
    () =>
      buildOrderFormInitialValues({
        editOrderState,
        order,
        isEditOrder,
        isEventOrCourse,
        voucherToEdit,
        voucherToRedeem,
        waitingListItem,
        account,
        isPendingEditedOrder,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityKey],
  );

  const contactOptional = !!editOrderState.orderPage?.contact_details_optional_in_backoffice;

  const validationSchema = useMemo(
    () => buildOrderValidationSchema(t, { isOneOfVoucherIntents, contactOptional }),
    [t, isOneOfVoucherIntents, contactOptional],
  );

  const { availableDatesForEvents, isLoadingDates } = useEditOrderDates({
    isEventOrCourse,
    accountId: editOrderState.account.id,
    orderPageId: editOrderState.orderPage.id,
    orderDetails: editOrderState.details,
    currentDate: initialValues.date,
  });

  const voucherDialogs = useVoucherDialogs({
    order,
    orderPage: editOrderState.orderPage,
    closeEditOrderDialog,
  });

  const handlePanelChange = (panel: EditOrderPanels) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : undefined);
  };

  const validateDateTimeError = (date: string, timeSeconds: number) => {
    if (isEventOrCourse && editOrderDialogIntent !== EditOrderDialogIntent.CreateVoucher) {
      const selectedDate = availableDatesForEvents.find((x) => x.date === date);
      if (!selectedDate) {
        setDateTimeError(DateTimeError.MISSING_DATE);
        return DateTimeError.MISSING_DATE;
      }
      const selectedTime = selectedDate.times.find((x) => x.seconds === timeSeconds);
      if (!selectedTime) {
        setDateTimeError(DateTimeError.MISSING_TIME);
        return DateTimeError.MISSING_TIME;
      }
    }
    setDateTimeError(undefined);
    return undefined;
  };

  const onSubmit = async (values: EditOrderFormValues) => {
    if (validateDateTimeError(values.date, values.timeSeconds)) {
      setExpandedPanel(EditOrderPanels.DateAndTimePanel);
      return;
    }

    setLoading(true);
    const duration = values.privateOrderEndTimeSeconds - values.timeSeconds;

    const payload = {
      parent_id: parentId,
      details: {
        ...editOrderState.details,
        duration: isEventOrCourse ? editOrderState.orderPage.duration : duration,
        resource_option_id: values.resourceOptionId,
      },
      date_and_time_details: !isEventOrCourse || isCourse ? { date: values.date, time: values.timeSeconds } : undefined,
      // referral_source is immutable after creation, so the edit payload intentionally never sends it.
      personal_details: {
        firstname: values.firstname,
        lastname: values.lastname,
        email: values.email,
        phone: values.phone,
        custom_personal_details: values.custom_personal_details,
        alternative_full_name: values.alternative_full_name,
      },
      order_id: '',
      new_parent_id: '' as string | undefined,
      account_id: editOrderState.account.id,
      experience_id: '',
      order_page_id: editOrderState.orderPage.id,
      language,
      employee_ids: [] as string[],
      comment: '' as string | undefined,
      voucher_id_to_redeem: undefined as string | undefined,
      waitlist_item_id_to_redeem: undefined as string | undefined,
      voucher_greeting_message: values.voucher_greeting_message || undefined,
      recipient_email: values.recipient_email || undefined,
      delivery_date: values.delivery_date || undefined,
      delivery_time: values.delivery_time || undefined,
      should_send_delayed: values.should_send_delayed,
      manual_resource_holds: undefined as ResourceHoldData[] | undefined,
      skip_resource_assignment: undefined as boolean | undefined,
      skip_customer_edit_notifications: undefined as boolean | undefined,
      course_id: undefined as string | undefined,
    };

    switch (editOrderDialogIntent) {
      case EditOrderDialogIntent.EditOrder: {
        payload.order_id = order.id!;
        payload.new_parent_id = isEventOrCourse
          ? getNewParentId(availableDatesForEvents, parentId, values.date, values.timeSeconds)
          : undefined;
        payload.employee_ids = values.selectedUsersIds;
        payload.comment = values.comment;
        payload.skip_customer_edit_notifications = values.skipCustomerEditNotifications;
        // Only sent when the admin actually changed it, so an ordinary edit never disturbs the
        // order's seating intent.
        payload.skip_resource_assignment =
          values.skipResourceAssignment !== initialValues.skipResourceAssignment
            ? values.skipResourceAssignment
            : undefined;
        payload.manual_resource_holds =
          JSON.stringify(canonicalHolds(values.selectedHolds)) !==
          JSON.stringify(canonicalHolds(initialValues.selectedHolds))
            ? values.selectedHolds
            : undefined;

        posthog.capture('Order Edited', { editor: 'admin', payload });
        // An untouched hold is not sent at all, so the existing deadline is only replaced when
        // the admin actually changed the pickers.
        const holdChanged =
          isPendingEditedOrder &&
          (values.paymentLinkHoldDate !== initialValues.paymentLinkHoldDate ||
            values.paymentLinkHoldTime !== initialValues.paymentLinkHoldTime);
        const orderResp = await executeWithConflictCheck(
          () =>
            OrderService.editOrder({
              ...payload,
              status: values.shouldUnarchive ? order.last_status : order.status,
              update_pending_hold: holdChanged || undefined,
              pending_expires_at: holdChanged
                ? holdExpiryIso(values.paymentLinkHoldDate, values.paymentLinkHoldTime, account.timezone)
                : undefined,
            }),
          t('calendar.edit_order.success'),
          () => setLoading(false),
        );

        if (updateOrder) updateOrder(orderResp);
        closeEditOrderDialog();
        break;
      }

      case EditOrderDialogIntent.CreateVoucher: {
        payload.language = language;
        payload.experience_id = editOrderState.experience.id;
        const voucher = await VoucherService.createAdminVoucher({
          ...payload,
          ...payload.personal_details,
          ...editOrderState.details,
          type: editOrderState.voucherType,
          comment: values.comment,
        });
        voucherDialogs.setVoucher(voucher);
        voucherDialogs.setVoucherConfirmationDialogIsOpen(true);
        break;
      }

      case EditOrderDialogIntent.EditVoucher: {
        posthog.capture('Voucher Edited', { editor: 'admin', payload });
        await VoucherService.editVoucher({
          voucher_id: voucherToEdit!.id,
          ...payload.personal_details,
          order_page_id: editOrderState.orderPage.id,
          comment: values.comment,
          voucher_greeting_message: payload.voucher_greeting_message,
          recipient_email: payload.recipient_email,
          should_send_delayed: payload.should_send_delayed,
          delivery_date: payload.delivery_date,
          delivery_time: payload.delivery_time,
          expiration_date: values.expirationDate || undefined,
          persons: payload.details.persons,
          custom_details: payload.details.custom_details,
        });
        closeEditOrderDialog();
        break;
      }

      default:
        throw new Error(`Unhandled intent: ${editOrderDialogIntent}`);
    }

    setLoading(false);

    if (onSubmitCallback) onSubmitCallback();
  };

  return (
    <Formik<EditOrderFormValues>
      key={entityKey}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}>
      {() => {
        return (
          <>
            <OrderFormSync
              isEditOrder={isEditOrder}
              isEventOrCourse={isEventOrCourse}
              isWaitlistItem={isWaitlistItem}
              hasParent={!!parent}
              orderPageIdOfOrder={order.order_page_id}
              availableDatesForEvents={availableDatesForEvents}
              onSubmitFailed={() => setExpandedPanel(EditOrderPanels.PersonalDetailsPanel)}
            />
            <Grid
              ref={scrollContainerRef}
              container
              flexDirection="column"
              gap={1.5}
              sx={{
                width: 'auto',
                marginInline: '-4px',
                paddingInline: '4px',
                paddingBlockStart: '4px',
                marginBlockStart: '-4px',
                '& > .MuiAccordion-root + .MuiAccordion-root, & > .MuiAccordion-root + .MuiAccordion-root.Mui-expanded':
                  {
                    marginTop: 0,
                  },
              }}
              style={{
                overflowY: 'auto',
                flex: 1,
                minHeight: 0,
                paddingBottom: isEditOrder ? '100px' : undefined,
              }}
              flexWrap="nowrap">
              {editOrderDialogIntent === EditOrderDialogIntent.EditVoucher && (
                <Grid>
                  <Alert severity="warning">{t('vouchers.editAlert')}</Alert>
                </Grid>
              )}
              <GuestRelationshipBanner guestOrderId={order?.guest_order_id} memberOrderId={order?.member_order_id} />
              {voucherToEdit?.type === VoucherType.MONETARY && (
                <VoucherRedemptionsPanel
                  expanded={expandedPanel}
                  handleChange={handlePanelChange}
                  voucher={voucherToEdit}
                />
              )}
              {!isMonetaryVoucher && (
                <Grid container flexDirection="column">
                  <ChooseWorkshopSelect
                    orderPages={orderPages}
                    editOrderState={editOrderState}
                    setOrderPage={setOrderPage}
                    setExpandedPanel={setExpandedPanel}
                  />
                </Grid>
              )}
              {!isOneOfVoucherIntents && (
                <DateAndTimePanel
                  availableDatesForEvents={availableDatesForEvents}
                  isLoadingDates={isLoadingDates}
                  expanded={expandedPanel}
                  handleChange={handlePanelChange}
                />
              )}
              {editOrderState &&
                !(isMonetaryVoucher && editOrderDialogIntent === EditOrderDialogIntent.EditVoucher) && (
                  <ExperienceDetailPanel
                    focusExtraItems={focusExtraItems}
                    hidePricing={isMonetaryVoucher}
                    detailsStepErrors={{}}
                    isLoadingDates={isLoadingDates}
                    expanded={expandedPanel}
                    handleChange={handlePanelChange}
                    onOrderUpdated={updateOrder}
                  />
                )}
              <PersonalDetailsPanel
                orderOrVoucher={order}
                isExistingOrder={editOrderState.order_id !== ''}
                orderPage={editOrderState.orderPage}
                isOneOfVoucherIntents={isOneOfVoucherIntents}
                setOrderCustomDetails={setOrderCustomDetails}
                expanded={expandedPanel}
                handleChange={handlePanelChange}
              />
              <ManagementPanel
                isEventOrderPage={isEventOrCourse}
                users={users}
                expanded={expandedPanel}
                handleChange={handlePanelChange}
                orderId={order?.id}
                isOneOfVoucherIntents={isOneOfVoucherIntents}
                orderOrVoucher={order}
                orderPage={editOrderState.orderPage}
              />
              <SeatingPanel
                isEventOrderPage={isEventOrCourse}
                expanded={expandedPanel}
                handleChange={handlePanelChange}
                orderId={order?.id}
                isOneOfVoucherIntents={isOneOfVoucherIntents}
                orderOrVoucher={order}
                orderPageId={editOrderState.orderPage.id}
                persons={editOrderState.details.persons}
                orderPage={editOrderState.orderPage}
              />
              <CommunicationTrackingPanel
                expanded={expandedPanel}
                handleChange={handlePanelChange}
                orderId={order?.id}
                voucherId={voucherToEdit?.id}
              />
              {isPendingEditedOrder && (
                <PaymentLinksPanel
                  expanded={expandedPanel}
                  handleChange={handlePanelChange}
                  pendingOrder={order as OrderData}
                />
              )}
              {dateTimeError ? (
                <Grid>
                  <Alert severity="error">{mapError(dateTimeError, t)}</Alert>
                </Grid>
              ) : null}
            </Grid>
            <EditOrderDialogFooter
              editOrderDialogIntent={editOrderDialogIntent}
              loading={loading}
              order={order}
              isEditOrder={isEditOrder}
              onConvertToVoucher={() => voucherDialogs.setConvertToVoucherDialogIsOpen(true)}
              voucherDialogs={voucherDialogs}
            />
          </>
        );
      }}
    </Formik>
  );
};
